import React, {useState} from "react";
import {Box, CheckBox, RadioButtonGroup, Select, Text, TextInput} from "grommet";
import Label from "./Label";
import InlineLinkIcon from "./InlineLinkIcon";
import Button from "./Button";
import { handleErrors } from "../utils/errors";

const getOAuthOptions = (oauths) => {
    let oauthOptions = oauths.map(oauth => ({
        value: oauth.shop_domain,
        label: oauth.name,
    }));
    const primaryOAuth = oauthOptions.splice(oauthOptions[0], 1);
    return [...primaryOAuth, ...oauthOptions];
}

const ExportCustomerInput = ({
    value, onChange, onCustomerIdChange, oAuthOptions,
    hubspotConnected, onHubSpotChange,
    ...rest
}) => {
    const [exportToShopify, setExportToShopify] = useState(false);
    const [exportToHubSpot, setExportToHubSpot] = useState(false);
    const [customerType, setCustomerType] = useState("new");
    const [shopifyCustomerId, setShopifyCustomerId] = useState("");

    const showShopify = oAuthOptions && oAuthOptions.length > 0;
    const selectedShopDomain = value || oAuthOptions?.[0]?.shop_domain;

    const handleCustomerTypeChange = (newType) => {
        setCustomerType(newType);
        if (newType === "new") {
            setShopifyCustomerId("");
            onCustomerIdChange && onCustomerIdChange(null);
        }
    };

    const handleCustomerIdChange = (id) => {
        setShopifyCustomerId(id);
        onCustomerIdChange && onCustomerIdChange(id || null);
    };

    return (
        <Box>
            <Label>Export customer</Label>
            <Box gap="small">
                {showShopify && (
                    <CheckBox
                        size="xsmall"
                        label="Shopify"
                        checked={exportToShopify}
                        onChange={e => {
                            setExportToShopify(e.target.checked);
                            onChange && onChange(e.target.checked ? oAuthOptions[0].shop_domain : null);
                            if (!e.target.checked) {
                                setCustomerType("new");
                                setShopifyCustomerId("");
                                onCustomerIdChange && onCustomerIdChange(null);
                            }
                        }}
                    />
                )}
                {exportToShopify && (
                    <Box margin={{left: 'medium', bottom: 'small'}} gap="small">
                        {oAuthOptions?.length > 1 && (
                            <Select
                                placeholder="Select store"
                                labelKey="label"
                                valueKey={{
                                    key: "value",
                                    reduce: true
                                }}
                                value={value}
                                options={getOAuthOptions(oAuthOptions)}
                                onChange={option => {
                                    onChange && onChange(option.value)
                                }}
                            />
                        )}
                        <RadioButtonGroup
                            name="customerType"
                            options={[
                                { label: "Create new customer in Shopify", value: "new" },
                                { label: "Link to existing Shopify customer", value: "existing" }
                            ]}
                            value={customerType}
                            onChange={(e) => handleCustomerTypeChange(e.target.value)}
                        />
                        {customerType === "existing" && (
                            <Box
                                pad="small"
                                border
                                round="xsmall"
                                gap="small"
                            >
                                <Box direction="row" align="center" gap="small">
                                    <Text size="small">Find the customer in Shopify:</Text>
                                    <Button
                                        event={{section: 'shopify_oauth_input', element: 'open_shopify_customers'}}
                                        plain
                                        href={`https://admin.shopify.com/store/${selectedShopDomain}/customers`}
                                        target="_blank"
                                        label={<Text size="small" color="brand">Open Shopify<InlineLinkIcon /></Text>}
                                    />
                                </Box>
                                <Box gap="xsmall">
                                    <Text size="small">
                                        Copy the customer ID from the URL:
                                    </Text>
                                    <Box
                                        direction="row"
                                        align="center"
                                        border
                                        pad={{horizontal: 'small', vertical: 'xsmall'}}
                                        round="xxsmall"
                                    >
                                        <Text size="small">.../customers/</Text>
                                        <TextInput
                                            plain
                                            placeholder="8024044503214"
                                            value={shopifyCustomerId}
                                            onChange={(e) => handleCustomerIdChange(e.target.value.replace(/\D/g, ''))}
                                            style={{
                                                padding: '2px 4px',
                                                fontWeight: 600,
                                                maxWidth: '140px'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
                {hubspotConnected && (
                    <CheckBox
                        size="xsmall"
                        label="HubSpot"
                        checked={exportToHubSpot}
                        onChange={e => {
                            setExportToHubSpot(e.target.checked);
                            onHubSpotChange && onHubSpotChange(e.target.checked);
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export const exportCustomerToPlatforms = async (customerId, { shopDomain, shopifyCustomerId, exportToHubSpot }) => {
    const exports = [];
    if (shopDomain) {
        exports.push(
            fetch(`/api/customers/${customerId}/export`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    platform: 'shopify',
                    shop_domain: shopDomain,
                    ...(shopifyCustomerId ? { shopify_customer_id: shopifyCustomerId } : {})
                }),
            }).then(handleErrors)
        );
    }
    if (exportToHubSpot) {
        exports.push(
            fetch(`/api/customers/${customerId}/export`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ platform: 'hubspot' }),
            }).then(handleErrors)
        );
    }
    return Promise.all(exports);
};

export default ExportCustomerInput;